import { useLocation } from "react-router-dom";
import Seo from "./Seo";

interface RouteMeta {
  title: string;
  description: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const breadcrumb = (name: string, path: string) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://snapshot.live/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name,
      item: `https://snapshot.live${path}`,
    },
  ],
});

export const PUBLIC_ROUTE_META: Record<string, RouteMeta> = {
  "/": {
    title: "Snapshot Live — Solar Powered Remote Site Cameras",
    description:
      "Solar-powered cameras deploy in minutes on any site — no power, no broadband. AI turns daily images into verified answers, alerts and evidence.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Snapshot Live",
      url: "https://snapshot.live/",
    },
  },
  "/about": {
    title: "About Snapshot Live — Our Team and Mission",
    description:
      "Meet the team behind Snapshot Live. We build autonomous solar camera systems for assurance, not surveillance — built to go where others can't.",
    jsonLd: breadcrumb("About", "/about"),
  },
  "/case-studies": {
    title: "Case Studies — Remote Site Monitoring in Action",
    description:
      "See how flood forecasting, event safety and infrastructure teams use Snapshot Live solar cameras to monitor sites without power or broadband.",
    jsonLd: breadcrumb("Case Studies", "/case-studies"),
  },
  "/features": {
    title: "Features — AI Site Monitoring Cameras | Snapshot Live",
    description:
      "Solar power, 4G connectivity, scheduled imagery and AI analysis. Explore the features that let Snapshot Live cameras run unattended anywhere.",
    jsonLd: breadcrumb("Features", "/features"),
  },
  "/pricing": {
    title: "Pricing — Snapshot Live Camera Plans",
    description:
      "Transparent plans for solar-powered remote site cameras and AI image credits. Find the package that fits your deployment.",
    jsonLd: breadcrumb("Pricing", "/pricing"),
  },
  "/applications": {
    title: "Applications — Construction, Utilities, Events & More",
    description:
      "Construction progress, flood monitoring, event safety, utilities and infrastructure — where Snapshot Live solar cameras are deployed.",
    jsonLd: breadcrumb("Applications", "/applications"),
  },
  "/equipment": {
    title: "Equipment Hire & Rental Terms | Snapshot Live",
    description:
      "Hire and rental terms for Snapshot Live camera equipment, including responsibilities, care of equipment and return conditions.",
    jsonLd: breadcrumb("Equipment", "/equipment"),
  },
  "/overview": {
    title: "Snapshot Live Brochure — Product Overview",
    description:
      "Download the Snapshot Live overview brochure: autonomous solar cameras, AI imagery analysis and deployment options for remote sites.",
    jsonLd: breadcrumb("Overview", "/overview"),
  },
  "/documentation": {
    title: "Help & Documentation | Snapshot Live",
    description:
      "User guides, video walkthroughs and frequently asked questions for deploying and managing Snapshot Live cameras.",
    jsonLd: breadcrumb("Documentation", "/documentation"),
  },
  "/terms": {
    title: "Terms of Use | Snapshot Live",
    description: "The terms governing use of the Snapshot Live platform and services.",
  },
  "/privacy": {
    title: "Privacy Policy | Snapshot Live",
    description: "How Snapshot Live collects, uses and protects personal data.",
  },
  "/cookies": {
    title: "Cookie Policy | Snapshot Live",
    description: "How Snapshot Live uses cookies and similar technologies on this website.",
  },
  "/disclaimer": {
    title: "Disclaimer | Snapshot Live",
    description: "Legal disclaimer covering the information and imagery provided by Snapshot Live.",
  },
  "/data-protection": {
    title: "Data Protection Policy | Snapshot Live",
    description: "Our data protection commitments, lawful bases for processing and data subject rights.",
  },
  "/environmental": {
    title: "Environmental Policy | Snapshot Live",
    description: "Our environmental commitments, from solar-powered hardware to reducing site visits.",
  },
  "/modern-slavery": {
    title: "Modern Slavery Statement | Snapshot Live",
    description: "Snapshot Live's statement on modern slavery and human trafficking in our supply chain.",
  },
  "/bribery-policy": {
    title: "Anti-Bribery & Anti-Corruption Policy | Snapshot Live",
    description: "Snapshot Live's anti-bribery and anti-corruption policy and reporting procedures.",
  },
};

const PRIVATE_PREFIXES = [
  "/dashboard",
  "/login",
  "/reset-password",
  "/verify-reset",
  "/invite",
  "/invite-contact",
  "/onboarding",
  "/proposal",
  "/proposal-signup",
  "/proposal-invite",
  "/partner-invite",
  "/showcase",
  "/test-showcase",
  "/info",
  "/map",
];

/**
 * Renders the right head metadata for the current route.
 * Public routes get a unique title/description/self-referencing canonical;
 * private app routes are marked noindex.
 */
export default function RouteSeo() {
  const { pathname } = useLocation();
  const path = pathname.length > 1 ? pathname.replace(/\/$/, "") : "/";
  const meta = PUBLIC_ROUTE_META[path];

  if (meta) {
    return (
      <Seo
        title={meta.title}
        description={meta.description}
        path={path}
        jsonLd={meta.jsonLd}
      />
    );
  }

  const isPrivate = PRIVATE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );

  if (isPrivate) {
    return (
      <Seo
        title="Snapshot Live"
        description="Snapshot Live platform."
        path={path}
        noindex
      />
    );
  }

  return null;
}